import { KLAVIYO_TOKENS } from '@portal/config/base';
import Script from 'next/script';
import { useStoreValue } from 'state/hooks/useStore';
import { useEffect, useRef } from 'react';
import {
  popupViewedEvent,
  emailSubmissionEvent,
  phoneSubmissionEvent
} from 'analytics/events/studio';
import { KLAVIYO_LOCATION, KLAVIYO_POPUP_TITLE } from 'analytics/constants';

// Utility function to determine Klaviyo popupTitle based on popupText
function getPopupTitle(popupText: string): string {
  if (!popupText) return '';

  const match = KLAVIYO_POPUP_TITLE.find(({ keywords }) =>
    keywords.some((keyword) => popupText.includes(keyword))
  );

  return match ? match.value : '';
}

const KlaviyoScript = () => {
  const store = useStoreValue();
  const observerRef = useRef<MutationObserver | null>(null);
  const companyId = KLAVIYO_TOKENS[store];

  useEffect(() => {
    let timeoutId: number | undefined;

    const handleKlaviyoForm = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const eventDetail = event.detail;
      const { metaData } = eventDetail || {};
      const isOpen = eventDetail?.type === 'open';
      const isSubmit = eventDetail?.type === 'submit';

      const a11yButton = document.getElementById('usntA40Toggle');

      // Handle open event / focus on a11y widget
      if (isOpen && a11yButton) {
        timeoutId = window.setTimeout(() => {
          a11yButton.setAttribute('tabindex', '0');
          a11yButton.focus();
        }, 250);
      }

      // Use a mutation observer to scrape DOM content on initial form open
      if (isOpen) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              const formElement = document.querySelector('.klaviyo-form');
              if (formElement) {
                // Check Klaviyo form DOM content to detect modal type
                const ctaButton = formElement.querySelector('button:not([type="reset"])');
                const ctaText = ctaButton?.textContent || undefined;
                const popupText = formElement.textContent?.trim() || null;
                const popupTitle = popupText && getPopupTitle(popupText);

                popupViewedEvent({ clickText: ctaText, popupTitle, location: KLAVIYO_LOCATION });
                observer.disconnect();
              }
            }
          });
        });
        observerRef.current = observer;
        observerRef.current.observe(document.body, {
          childList: true,
          subtree: true
        });

        setTimeout(() => {
          if (observerRef.current) {
            observerRef.current.disconnect();
          }
        }, 10000); // Disconnect observer after some time if data not found
      }

      // Fire event when popup form is submitted
      if (isSubmit) {
        const formElement = document.querySelector('.klaviyo-form');

        // Get CTA text
        const ctaButton = formElement?.querySelector('button:not([type="reset"])');
        const ctaText = ctaButton?.textContent || undefined;

        // Checking Klaviyo text to detect modal's type
        const popupText = formElement?.textContent?.trim() || null;
        const popupTitle = popupText && getPopupTitle(popupText);

        const email = metaData?.$email || metaData?.email;
        const phone = metaData?.$phone_number || metaData?.phone_number;

        if (email) {
          emailSubmissionEvent({
            clickText: ctaText,
            popupTitle,
            email,
            location: KLAVIYO_LOCATION
          });
        }

        if (phone) {
          phoneSubmissionEvent({
            clickText: ctaText,
            popupTitle,
            phone,
            location: KLAVIYO_LOCATION
          });
        }
      }
    };

    window.addEventListener('klaviyoForms', handleKlaviyoForm);
    return () => {
      window.removeEventListener('klaviyoForms', handleKlaviyoForm);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <>
      {companyId && <Script src={`https://static.klaviyo.com/onsite/js/${companyId}/klaviyo.js`} />}
    </>
  );
};

export default KlaviyoScript;
